import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const JobCard = ({ job, onViewResponses, onMessage, onStatusUpdate }) => {
  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-success/10 text-success',
      'ongoing': 'bg-warning/10 text-warning',
      'completed': 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getUrgencyIndicator = (urgency) => {
    if (urgency === 'high') {
      return <span className="w-2 h-2 bg-error rounded-full animate-pulse" />;
    }
    return null;
  };

  const formatBudget = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div 
      className="bg-white border-2 border-slate-200 rounded-lg p-4 md:p-5 lg:p-6 hover:elevation-md transition-smooth"
      style={{
        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground truncate">
              {job?.title}
            </h3>
            {getUrgencyIndicator(job?.urgency)}
          </div>
          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            <span className={`caption px-2 py-1 rounded ${getStatusColor(job?.status)}`}>
              {job?.statusLabel}
            </span>
            <div className="flex items-center gap-1 caption text-muted-foreground">
              <Icon name="MapPin" size={14} />
              <span>{job?.location}</span>
            </div>
          </div>
        </div>
        <Link
          to={`/job-details-and-applications?id=${job?.id}`}
          className="flex-shrink-0"
        >
          <Button variant="ghost" size="icon">
            <Icon name="ExternalLink" size={18} />
          </Button>
        </Link>
      </div>
      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
        {job?.description}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
        <div>
          <p className="caption text-muted-foreground mb-1">Budget</p>
          <p className="font-medium text-sm md:text-base text-foreground">
            {formatBudget(job?.budget)}
          </p>
        </div>
        <div>
          <p className="caption text-muted-foreground mb-1">Startdatum</p>
          <p className="font-medium text-sm md:text-base text-foreground">
            {formatDate(job?.startDate)}
          </p>
        </div>
        <div>
          <p className="caption text-muted-foreground mb-1">Duur</p>
          <p className="font-medium text-sm md:text-base text-foreground">
            {job?.duration}
          </p>
        </div>
        <div>
          <p className="caption text-muted-foreground mb-1">Type</p>
          <p className="font-medium text-sm md:text-base text-foreground">
            {job?.workType}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {job?.applicants?.slice(0, 3)?.map((applicant, index) => (
                <div
                  key={index}
                  className="w-8 h-8 rounded-full border-2 border-card overflow-hidden"
                >
                  <Image
                    src={applicant?.avatar}
                    alt={applicant?.avatarAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            <span className="caption text-muted-foreground">
              {job?.applicationCount} {job?.applicationCount === 1 ? 'reactie' : 'reacties'}
            </span>
          </div>
          {job?.affiliateInfo && (
            <div className="flex items-center gap-1 caption text-success">
              <Icon name="Users" size={14} />
              <span>Via {job?.affiliateInfo?.name}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMessage(job?.id)}
            iconName="MessageSquare"
            iconSize={16}
            className="hidden md:flex"
          >
            Berichten
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewResponses(job?.id)}
            iconName="Users"
            iconSize={16}
          >
            Bekijk reacties
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobCard;